import EducationalPlatform from "@/Components/EducationalPlatform";
import GroupSlider from "@/Components/GroupSlider";
import AboutusSection from "@/Components/Home/AboutusSection";
import DreamUniversitySection from "@/Components/Home/DreamUniversity/DreamUniversitySection";
import EquivalentRateHero from "@/Components/Home/EquivalentRateHero";
import MainSlider from "@/Components/MainSlider";
import CourseExplorer from "@/Components/Navigation/CourseExplorer";
import PopupModal from "@/Components/LazyPopupModal";
import SuccessPartners from "@/Components/SuccessPartners";
import TeachersSection from "@/Components/TeachersSection";
import UserCard from "@/Components/User/UserCard";
import MainContent from "@/Pages/Frontend/Partials/MainContent";
import { getIcon, getImage } from "@/constants";
import { localeType, PageProps, Setting, User } from "@/types";
import { usePage } from "@inertiajs/react";
import { Category, Grade, Slide, Stage, Subject } from "@/types/queries";
import { map } from "lodash";
import { useTranslation } from "react-i18next";
import { ReactSVG } from "react-svg";
import { motion } from "framer-motion";

// Animation variants for sections
const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
        },
    },
};

export default function ({
    slides,
    teachers,
    currentLang,
    setting,
    categories,
    grades,
    stages,
    subjects,
}: PageProps<{
    currentLang: localeType;
    slides: Slide[];
    setting: Setting;
    teachers: User[];
    categories: Category[];
    grades: Grade[];
    stages: Stage[];
    subjects: Subject[];
}>) {
    const { t } = useTranslation();
    const { auth } = usePage<PageProps>().props;
    return (
        <MainContent>
            {setting.show_popup && <PopupModal />}
            {slides && <MainSlider slides={slides} />}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
            >
                <CourseExplorer
                    stages={stages}
                    categories={categories}
                    grades={grades}
                    subjects={subjects}
                    home={true}
                />
            </motion.div>
            {/* Equivalent-rate calculator: gated. Guests see the marketing
                hero; signed-in users get the live universities calculator.
                The old input-only EquivalentRate sections are retired. */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
            >
                {auth?.id ? <DreamUniversitySection /> : <EquivalentRateHero />}
            </motion.div>
            {/* exam appointments */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
                className="bg-white overflow-hidden p-4 sm:p-8  rounded-3xl"
            >
                <div
                    className="mx-auto p-4 sm:p-8 bg-local rounded-3xl"
                    style={{
                        backgroundImage: `url(${getImage("bg.jpg")})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "600px",
                    }}
                >
                    <div className="mx-auto grid  grid-cols-1 gap-x-8 gap-y-10  lg:grid-cols-2 justify-center items-center">
                        <div className="lg:max-w-lg flex flex-col gap-y-6 ps-8">
                            <p className="text-lg font-bold tracking-tight text-prim-900 sm:text-4xl break-words leading-loose first-letter:uppercase">
                                {t(
                                    "check_lateset_appointments_and_book_ur_seat",
                                )}
                            </p>
                            <div className="flex gap-2">
                                <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                                    <a
                                        target="_blank"
                                        href={`https://book.ielts.idp.com/account/signin`}
                                        className="w-auto text-center rounded-3xl p-3 px-8 text-white bg-theme-800 hover:bg-theme-600 capitalize"
                                    >
                                        {t("book_ielts")}
                                    </a>
                                </div>
                                <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                                    <a
                                        target="_blank"
                                        href={`https://wa.me/96599161629`}
                                        className="w-auto text-center rounded-3xl p-3 px-8 text-white bg-theme-800 hover:bg-theme-600 capitalize"
                                    >
                                        {t("book_icdl")}
                                    </a>
                                </div>
                            </div>
                            <div className="flex flex-col  justify-start items-start gap-4">
                                <p className="text-sm md:text-md font-bold tracking-tight text-prim-900  break-words leading-loose first-letter:uppercase">
                                    {t("for_more_help_contactus")}
                                </p>
                                <a
                                    target="_blank"
                                    // href={whatsappUrl(setting.whatsapp)}
                                    href={`https://wa.me/96592272825`}
                                    className="w-auto text-center rounded-3xl p-3 px-8 text-theme-600 border border-theme-800 hover:text-white hover:bg-theme-600 capitalize"
                                >
                                    {t("or_contact_us")}
                                </a>
                            </div>
                        </div>
                        <div className="flex justify-center items-center">
                            <img
                                loading="lazy"
                                src={getImage("join_us.png")}
                                alt={setting.name}
                                className="w-full h-auto object-cover"
                            />
                        </div>
                    </div>
                </div>
            </motion.div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
            >
                <EducationalPlatform />
            </motion.div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
            >
                <AboutusSection />
            </motion.div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
            >
                <SuccessPartners />
            </motion.div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
            >
                <TeachersSection teachers={teachers} />
            </motion.div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
                className="bg-white py-8 rounded-3xl"
            >
                <div className="mx-auto  px-6 lg:px-8">
                    {/* Header Section */}
                    <div className="flex flex-1 flex-col justify-center items-center gap-y-2 mb-6">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl capitalize">
                            {t("success_partners")}
                        </h2>

                        {/* Decorative underline */}
                        <ReactSVG
                            src={getIcon("title_arrow.svg")}
                            className="relative w-20 text-orange-700 ltr:rotate-180"
                        />
                    </div>
                    <GroupSlider />
                </div>
            </motion.div>
        </MainContent>
    );
}
